import { useState, useEffect, useCallback } from 'react';
import { supabase, Transaction, Category, CategorySuggestion, CategorizationSuggestion } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export function useSmartCategorization() {
  const { effectiveUserId } = useAuth();
  const [suggestions, setSuggestions] = useState<Map<string, CategorySuggestion>>(new Map());
  const [isLearning, setIsLearning] = useState(false);

  async function getSuggestionForTransaction(transaction: Transaction): Promise<CategorySuggestion | null> {
    if (!effectiveUserId || transaction.category_id) return null;

    const existingSuggestion = await supabase
      .from('categorization_suggestions')
      .select('*, categories(name)')
      .eq('transaction_id', transaction.id)
      .is('was_accepted', null)
      .maybeSingle();

    if (existingSuggestion.data) {
      return {
        category_id: existingSuggestion.data.suggested_category_id,
        category_name: (existingSuggestion.data as any).categories?.name || '',
        confidence_score: existingSuggestion.data.confidence_score
      };
    }

    const { data, error } = await supabase.rpc('suggest_category_for_transaction', {
      p_user_id: effectiveUserId,
      p_transaction_label: transaction.label
    });

    if (error || !data || data.length === 0) {
      return null;
    }

    const topSuggestion = data[0];

    if (topSuggestion.confidence_score > 0.05) {
      await supabase
        .from('categorization_suggestions')
        .insert({
          user_id: effectiveUserId,
          transaction_id: transaction.id,
          suggested_category_id: topSuggestion.category_id,
          confidence_score: topSuggestion.confidence_score,
          was_accepted: null
        });

      return topSuggestion;
    }

    return null;
  }

  async function loadSuggestionsForTransactions(transactions: Transaction[], limit: number = 50) {
    if (!effectiveUserId) return;

    const newSuggestions = new Map<string, CategorySuggestion>();
    const uncategorizedTransactions = transactions.filter(t => !t.category_id).slice(0, limit);

    if (uncategorizedTransactions.length === 0) {
      setSuggestions(newSuggestions);
      return;
    }

    const existingSuggestions = await supabase
      .from('categorization_suggestions')
      .select('*, categories(name)')
      .in('transaction_id', uncategorizedTransactions.map(t => t.id))
      .is('was_accepted', null);

    if (existingSuggestions.data) {
      existingSuggestions.data.forEach(suggestion => {
        newSuggestions.set(suggestion.transaction_id, {
          category_id: suggestion.suggested_category_id,
          category_name: (suggestion as any).categories?.name || '',
          confidence_score: suggestion.confidence_score
        });
      });
    }

    const transactionsNeedingSuggestions = uncategorizedTransactions.filter(
      t => !newSuggestions.has(t.id)
    );

    if (transactionsNeedingSuggestions.length > 0) {
      const { data: batchSuggestions, error } = await supabase.rpc('generate_suggestions_batch', {
        p_user_id: effectiveUserId,
        p_transaction_ids: transactionsNeedingSuggestions.map(t => t.id)
      });

      if (!error && batchSuggestions) {
        const suggestionsToInsert = batchSuggestions.map((s: any) => ({
          transaction_id: s.transaction_id,
          suggested_category_id: s.suggested_category_id,
          confidence_score: s.confidence_score
        }));

        if (suggestionsToInsert.length > 0) {
          await supabase.rpc('upsert_suggestions_batch', {
            p_user_id: effectiveUserId,
            p_suggestions: suggestionsToInsert
          });

          batchSuggestions.forEach((s: any) => {
            newSuggestions.set(s.transaction_id, {
              category_id: s.suggested_category_id,
              category_name: s.suggested_category_name,
              confidence_score: s.confidence_score
            });
          });
        }
      }
    }

    setSuggestions(newSuggestions);
  }

  async function acceptSuggestion(transactionId: string, categoryId: string, transactionLabel: string) {
    if (!effectiveUserId) return;

    setIsLearning(true);

    try {
      // Check if the category is an internal transfer
      const { data: category } = await supabase
        .from('categories')
        .select('is_internal_transfer')
        .eq('id', categoryId)
        .single();

      const isInternalTransfer = category?.is_internal_transfer || false;

      await supabase
        .from('transactions')
        .update({
          category_id: categoryId,
          is_internal_transfer: isInternalTransfer
        })
        .eq('id', transactionId)
        .eq('user_id', effectiveUserId);

      await supabase
        .from('categorization_suggestions')
        .update({
          was_accepted: true,
          feedback_at: new Date().toISOString()
        })
        .eq('transaction_id', transactionId);

      await supabase.rpc('learn_from_transaction', {
        p_user_id: effectiveUserId,
        p_transaction_label: transactionLabel,
        p_category_id: categoryId
      });

      setSuggestions(prev => {
        const newMap = new Map(prev);
        newMap.delete(transactionId);
        return newMap;
      });
    } finally {
      setIsLearning(false);
    }
  }

  async function rejectSuggestion(transactionId: string) {
    if (!effectiveUserId) return;

    await supabase
      .from('categorization_suggestions')
      .update({
        was_accepted: false,
        feedback_at: new Date().toISOString()
      })
      .eq('transaction_id', transactionId);

    setSuggestions(prev => {
      const newMap = new Map(prev);
      newMap.delete(transactionId);
      return newMap;
    });
  }

  async function learnFromManualCategorization(transactionLabel: string, categoryId: string) {
    if (!effectiveUserId) return;

    setIsLearning(true);
    try {
      await supabase.rpc('learn_from_transaction', {
        p_user_id: effectiveUserId,
        p_transaction_label: transactionLabel,
        p_category_id: categoryId
      });
    } finally {
      setIsLearning(false);
    }
  }

  async function trainInitialModel(transactions: Transaction[]) {
    if (!effectiveUserId) {
      console.error('trainInitialModel: No user ID');
      throw new Error('Utilisateur non connecté');
    }

    setIsLearning(true);
    try {
      const categorizedTransactions = transactions
        .filter(t => t.category_id)
        .map(t => ({
          label: t.label,
          category_id: t.category_id
        }));

      console.log(`Training model with ${categorizedTransactions.length} transactions`);
      console.log('First transaction:', categorizedTransactions[0]);

      if (categorizedTransactions.length === 0) {
        throw new Error('Aucune transaction catégorisée pour l\'entraînement');
      }

      const { data, error } = await supabase.rpc('learn_from_transactions_batch', {
        p_user_id: effectiveUserId,
        p_transactions: categorizedTransactions
      });

      if (error) {
        console.error('Error training model:', error);
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw new Error(`Erreur d'entraînement: ${error.message || 'Erreur inconnue'}`);
      }

      console.log('Model training successful', data);
    } finally {
      setIsLearning(false);
    }
  }

  return {
    suggestions,
    isLearning,
    getSuggestionForTransaction,
    loadSuggestionsForTransactions,
    acceptSuggestion,
    rejectSuggestion,
    learnFromManualCategorization,
    trainInitialModel
  };
}
